import React, { useEffect, useState, useRef } from 'react';
import { Plus, Microscope, Activity, Brain, Eye, Scissors, ExternalLink } from 'lucide-react';
// --- Types ---
type Point = {
  x: number;
  y: number;
};
type Particle = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  type: 'blood' | 'neuron';
};
// --- Constants ---
const STAGES = [{
  title: 'Fig. I: Osteology',
  text: 'Something is taking shape.',
  latin: 'Structura Ossium',
  note: 'The Framework',
  layer: 0
}, {
  title: 'Fig. II: Myology',
  text: 'It begins to move.',
  latin: 'Systema Musculare',
  note: 'The Movement',
  layer: 1
}, {
  title: 'Fig. III: Angiology',
  text: 'It comes alive.',
  latin: 'Systema Vasculare',
  note: 'The Flow',
  layer: 2
}, {
  title: 'Fig. IV: Neurologia',
  text: 'You are invited to feel it.',
  latin: 'Systema Nervosum',
  note: 'The Connection',
  isFinal: true,
  layer: 3
}];
export function App() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [stage, setStage] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [flash, setFlash] = useState(false);
  const [touchStart, setTouchStart] = useState<number>(0);
  const mouse = useRef<Point>({
    x: -1000,
    y: -1000
  });
  const time = useRef(0);
  const animationFrame = useRef<number>(0);
  const particles = useRef<Particle[]>([]);
  const isLongPress = useRef(false);
  const longPressTimer = useRef<NodeJS.Timeout | null>(null);
  // --- Anatomical Drawing Logic ---
  const drawBone = (ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, angle: number) => {
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(angle);
    ctx.fillStyle = '#F5E6D3';
    ctx.strokeStyle = '#8D6E63';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(-width / 2, -height / 2);
    ctx.quadraticCurveTo(0, -height / 2 - 5, width / 2, -height / 2);
    ctx.lineTo(width / 4, height / 2);
    ctx.quadraticCurveTo(0, height / 2 + 5, -width / 4, height / 2);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    // Stippling
    ctx.fillStyle = '#8D6E63';
    for (let i = 0; i < 5; i++) {
      ctx.fillRect((Math.random() - 0.5) * width * 0.6, (Math.random() - 0.5) * height * 0.8, 1, 1);
    }
    ctx.restore();
  };
  const drawMuscle = (ctx: CanvasRenderingContext2D, x1: number, y1: number, x2: number, y2: number, width: number) => {
    const dx = x2 - x1;
    const dy = y2 - y1;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const angle = Math.atan2(dy, dx);
    ctx.save();
    ctx.translate(x1, y1);
    ctx.rotate(angle);
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.quadraticCurveTo(dist / 2, -width, dist, 0);
    ctx.quadraticCurveTo(dist / 2, width, 0, 0);
    ctx.fillStyle = 'rgba(139, 0, 0, 0.15)';
    ctx.fill();
    ctx.strokeStyle = 'rgba(139, 0, 0, 0.3)';
    ctx.lineWidth = 0.5;
    for (let i = 0; i < dist; i += 4) {
      ctx.beginPath();
      ctx.moveTo(i, -width / 2 * Math.sin(i / dist * Math.PI));
      ctx.lineTo(i + 10, width / 2 * Math.sin(i / dist * Math.PI));
      ctx.stroke();
    }
    ctx.restore();
  };
  const drawBranch = (ctx: CanvasRenderingContext2D, x: number, y: number, len: number, angle: number, depth: number, type: 'vein' | 'nerve') => {
    if (depth === 0) return;
    const endX = x + len * Math.cos(angle);
    const endY = y + len * Math.sin(angle);
    ctx.beginPath();
    ctx.moveTo(x, y);
    const cpX = x + len / 2 * Math.cos(angle + 0.2);
    const cpY = y + len / 2 * Math.sin(angle + 0.2);
    ctx.quadraticCurveTo(cpX, cpY, endX, endY);
    if (type === 'vein') {
      ctx.strokeStyle = depth % 2 === 0 ? 'rgba(30, 58, 95, 0.4)' : 'rgba(139, 0, 0, 0.4)';
      ctx.lineWidth = depth * 0.8;
    } else {
      ctx.strokeStyle = 'rgba(218, 165, 32, 0.5)';
      ctx.lineWidth = depth * 0.5;
    }
    ctx.stroke();
    // Spawn particles
    if (Math.random() > 0.98) {
      particles.current.push({
        x: x,
        y: y,
        vx: (Math.random() - 0.5) * 0.5,
        vy: (Math.random() - 0.5) * 0.5,
        life: 100,
        type: type === 'vein' ? 'blood' : 'neuron'
      });
    }
    const subLen = len * 0.75;
    drawBranch(ctx, endX, endY, subLen, angle - 0.3, depth - 1, type);
    drawBranch(ctx, endX, endY, subLen, angle + 0.4, depth - 1, type);
  };
  const drawHeart = (ctx: CanvasRenderingContext2D, x: number, y: number, scale: number) => {
    ctx.save();
    ctx.translate(x, y);
    ctx.scale(scale, scale);
    // Heart Shape
    ctx.beginPath();
    ctx.moveTo(0, -30);
    ctx.bezierCurveTo(30, -50, 60, -20, 0, 50);
    ctx.bezierCurveTo(-60, -20, -30, -50, 0, -30);
    // Gradient
    const grad = ctx.createRadialGradient(-10, -10, 5, 0, 0, 60);
    grad.addColorStop(0, '#DC143C');
    grad.addColorStop(1, '#8B0000');
    ctx.fillStyle = grad;
    ctx.fill();
    ctx.strokeStyle = '#3E2723';
    ctx.lineWidth = 1;
    ctx.stroke();
    // Arteries on heart
    ctx.beginPath();
    ctx.moveTo(0, -30);
    ctx.quadraticCurveTo(10, 0, 0, 40);
    ctx.strokeStyle = 'rgba(255,255,255,0.2)';
    ctx.stroke();
    ctx.restore();
  };
  const drawEye = (ctx: CanvasRenderingContext2D, x: number, y: number, lookAt: Point) => {
    ctx.save();
    ctx.translate(x, y);
    // Sclera
    ctx.beginPath();
    ctx.ellipse(0, 0, 20, 12, 0, 0, Math.PI * 2);
    ctx.fillStyle = '#F5E6D3';
    ctx.fill();
    ctx.strokeStyle = '#3E2723';
    ctx.stroke();
    // Iris tracking
    const dx = lookAt.x - (window.innerWidth / 2 + x);
    const dy = lookAt.y - (window.innerHeight / 2 + y);
    const angle = Math.atan2(dy, dx);
    const dist = Math.min(5, Math.sqrt(dx * dx + dy * dy) / 50);
    const pupilX = Math.cos(angle) * dist;
    const pupilY = Math.sin(angle) * dist;
    // Iris
    ctx.beginPath();
    ctx.arc(pupilX, pupilY, 6, 0, Math.PI * 2);
    ctx.fillStyle = '#4682B4';
    ctx.fill();
    // Pupil
    ctx.beginPath();
    ctx.arc(pupilX, pupilY, 2.5, 0, Math.PI * 2);
    ctx.fillStyle = '#000';
    ctx.fill();
    // Blink (simple lid)
    if (Math.sin(time.current * 2) > 0.98) {
      ctx.fillStyle = '#E8DCC4';
      ctx.beginPath();
      ctx.ellipse(0, 0, 21, 13, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.moveTo(-20, 0);
      ctx.lineTo(20, 0);
      ctx.strokeStyle = '#3E2723';
      ctx.stroke();
    }
    ctx.restore();
  };
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    const animate = () => {
      if (!ctx || !canvas) return;
      time.current += 0.01;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;
      const scale = Math.min(canvas.width, canvas.height) / 800;
      // Heartbeat & Breathing
      const heartBeat = (Math.sin(time.current * 8) + 1) * 0.5; // Fast beat
      const breath = Math.sin(time.current) * 0.02 + 1;
      ctx.save();
      ctx.translate(centerX, centerY);
      ctx.scale(scale * breath, scale * breath);
      // --- LAYER 0: SKELETON ---
      if (stage >= 0) {
        ctx.globalAlpha = 1;
        for (let i = -5; i < 6; i++) drawBone(ctx, 0, i * 40, 30, 35, 0);
        for (let i = -4; i < 4; i++) {
          // Ribs expanding with breath
          const ribScale = 1 + Math.sin(time.current) * 0.05;
          ctx.save();
          ctx.scale(ribScale, 1);
          ctx.beginPath();
          ctx.moveTo(-15, i * 40);
          ctx.bezierCurveTo(-100, i * 40 - 20, -180, i * 40 + 50, -60, i * 40 + 100);
          ctx.strokeStyle = '#F5E6D3';
          ctx.lineWidth = 15;
          ctx.lineCap = 'round';
          ctx.stroke();
          ctx.strokeStyle = '#8D6E63';
          ctx.lineWidth = 1;
          ctx.stroke();
          ctx.beginPath();
          ctx.moveTo(15, i * 40);
          ctx.bezierCurveTo(100, i * 40 - 20, 180, i * 40 + 50, 60, i * 40 + 100);
          ctx.strokeStyle = '#F5E6D3';
          ctx.lineWidth = 15;
          ctx.stroke();
          ctx.strokeStyle = '#8D6E63';
          ctx.lineWidth = 1;
          ctx.stroke();
          ctx.restore();
        }
        // Skull Eye
        drawEye(ctx, 0, -220, mouse.current);
      }
      // --- LAYER 1: MUSCLES ---
      if (stage >= 1) {
        ctx.globalAlpha = stage === 1 ? 1 : 0.4;
        for (let i = -4; i < 3; i++) {
          drawMuscle(ctx, -100, i * 40 + 20, -100, (i + 1) * 40 + 20, 30);
          drawMuscle(ctx, 100, i * 40 + 20, 100, (i + 1) * 40 + 20, 30);
        }
        drawMuscle(ctx, -20, -150, -120, -50, 60);
        drawMuscle(ctx, 20, -150, 120, -50, 60);
      }
      // --- LAYER 2: CIRCULATORY ---
      if (stage >= 2) {
        ctx.globalAlpha = stage === 2 ? 1 : 0.8;
        // Beating Heart
        const beatScale = 1 + (heartBeat > 0.8 ? 0.1 : 0);
        drawHeart(ctx, -20, -20, beatScale);
        drawBranch(ctx, -20, -60, 60, -Math.PI / 2, 5, 'vein');
        drawBranch(ctx, -20, -20, 80, Math.PI, 4, 'vein');
        drawBranch(ctx, 20, -20, 80, 0, 4, 'vein');
      }
      // --- LAYER 3: NERVOUS ---
      if (stage >= 3) {
        ctx.globalAlpha = 1;
        ctx.shadowBlur = 15 + Math.random() * 5;
        ctx.shadowColor = '#DAA520';
        ctx.strokeStyle = '#DAA520';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(0, -200);
        ctx.lineTo(0, 200);
        ctx.stroke();
        ctx.shadowBlur = 0;
        for (let i = -5; i < 5; i++) {
          drawBranch(ctx, 0, i * 40, 50, 0, 3, 'nerve');
          drawBranch(ctx, 0, i * 40, 50, Math.PI, 3, 'nerve');
        }
      }
      ctx.restore();
      // Particles
      particles.current.forEach((p, i) => {
        p.x += p.vx;
        p.y += p.vy;
        p.life--;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.type === 'blood' ? 2 : 1, 0, Math.PI * 2);
        ctx.fillStyle = p.type === 'blood' ? '#DC143C' : '#FFD700';
        ctx.fill();
        if (p.life <= 0) particles.current.splice(i, 1);
      });
      // Mouse Lens
      if (mouse.current.x > 0) {
        ctx.save();
        ctx.beginPath();
        ctx.arc(mouse.current.x, mouse.current.y, 80, 0, Math.PI * 2);
        ctx.strokeStyle = '#3E2723';
        ctx.lineWidth = 2;
        ctx.stroke();
        ctx.restore();
      }
      animationFrame.current = requestAnimationFrame(animate);
    };
    animate();
    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationFrame.current);
    };
  }, [stage]);
  // --- Interaction Handlers ---
  const handleTouchStart = (e: React.TouchEvent | React.MouseEvent) => {
    const x = 'touches' in e ? e.touches[0].clientX : (e as React.MouseEvent).clientX;
    const y = 'touches' in e ? e.touches[0].clientY : (e as React.MouseEvent).clientY;
    mouse.current = {
      x,
      y
    };
    setTouchStart(Date.now());
    longPressTimer.current = setTimeout(() => {
      isLongPress.current = true;
    }, 500);
  };
  const handleTouchMove = (e: React.TouchEvent | React.MouseEvent) => {
    const x = 'touches' in e ? e.touches[0].clientX : (e as React.MouseEvent).clientX;
    const y = 'touches' in e ? e.touches[0].clientY : (e as React.MouseEvent).clientY;
    mouse.current = {
      x,
      y
    };
  };
  const handleTouchEnd = () => {
    mouse.current = {
      x: -1000,
      y: -1000
    };
    isLongPress.current = false;
    if (longPressTimer.current) clearTimeout(longPressTimer.current);
    const touchDuration = Date.now() - touchStart;
    // Only advance if not clicking a link
    if (touchDuration < 300 && !isTransitioning && stage < STAGES.length - 1) {
      advanceStage();
    }
  };
  const advanceStage = () => {
    setIsTransitioning(true);
    setFlash(true);
    setTimeout(() => setFlash(false), 300);
    setTimeout(() => {
      setStage(prev => prev + 1);
      setIsTransitioning(false);
    }, 600);
  };
  const currentContent = STAGES[stage];
  return <div className="relative w-full h-full overflow-hidden select-none medical-grid" onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd} onMouseDown={handleTouchStart} onMouseMove={handleTouchMove} onMouseUp={handleTouchEnd} data-id="element-0">
      {/* X-Ray Flash Overlay */}
      {flash && <div className="absolute inset-0 bg-white z-50 animate-flash pointer-events-none" data-id="element-1" />}

      {/* Generative Background */}
      <canvas ref={canvasRef} className="absolute inset-0 z-0 block" data-id="element-2" />

      {/* UI Overlay */}
      <div className="absolute inset-0 z-10 flex flex-col items-center justify-center p-8 text-center pointer-events-none" data-id="element-3">
        <div key={stage} className={`transition-all duration-1000 ${isTransitioning ? 'opacity-0 blur-sm translate-y-4' : 'opacity-100 blur-0 translate-y-0'}`} data-id="element-4">
          {/* Medical Label Header */}
          <div className="mb-6 inline-block border-b-2 border-sepia/30 pb-2 relative" data-id="element-5">
            <span className="font-mono-medical text-xs tracking-[0.2em] text-sepia/60 uppercase block mb-1" data-id="element-6">
              {currentContent.title}
            </span>
            <span className="font-medical text-lg text-sepia italic" data-id="element-7">
              {currentContent.latin}
            </span>
            {/* Handwritten Note */}
            <span className="absolute -right-24 -top-8 font-handwritten text-red-800 text-xl w-32 rotate-12 opacity-80" data-id="element-8">
              {currentContent.note}
            </span>
          </div>

          {/* Main Text */}
          <h1 className="text-4xl md:text-6xl font-serif mb-6 leading-tight text-ink drop-shadow-sm" data-id="element-9">
            {currentContent.text}
          </h1>

          {/* Final Coupon Card - CLICKABLE */}
          {currentContent.isFinal && <div className="animate-enter mt-8 pointer-events-auto" data-id="element-10">
              <a href="https://www.stadthalle.com/de/events/alle-events/51100/Koerperwelten-Am-Puls-der-Zeit" target="_blank" rel="noopener noreferrer" className="block coupon-link" data-id="element-11">
                <div className="bg-[#F5E6D3] border-2 border-[#3E2723] p-1 max-w-xs mx-auto shadow-2xl transform rotate-1 relative group" data-id="element-12">
                  {/* Corner screws/pins */}
                  <div className="absolute top-2 left-2 w-2 h-2 rounded-full bg-[#3E2723] opacity-50" data-id="element-13"></div>
                  <div className="absolute top-2 right-2 w-2 h-2 rounded-full bg-[#3E2723] opacity-50" data-id="element-14"></div>
                  <div className="absolute bottom-2 left-2 w-2 h-2 rounded-full bg-[#3E2723] opacity-50" data-id="element-15"></div>
                  <div className="absolute bottom-2 right-2 w-2 h-2 rounded-full bg-[#3E2723] opacity-50" data-id="element-16"></div>

                  {/* Official Stamp */}
                  <div className="absolute -right-4 -bottom-4 w-20 h-20 border-4 border-red-800 rounded-full flex items-center justify-center opacity-70 transform -rotate-12 mix-blend-multiply bg-[#F5E6D3]" data-id="element-17">
                    <span className="text-red-800 font-mono-medical text-[10px] text-center font-bold" data-id="element-18">
                      OFFICIAL
                      <br data-id="element-19" />
                      SPECIMEN
                      <br data-id="element-20" />
                      VIENNA
                    </span>
                  </div>

                  <div className="border border-[#3E2723] border-dashed p-6 relative overflow-hidden" data-id="element-21">
                    <div className="flex justify-center mb-4 space-x-4 text-[#3E2723]" data-id="element-22">
                      <Microscope className="w-6 h-6" data-id="element-23" />
                      <Brain className="w-6 h-6" data-id="element-24" />
                      <Activity className="w-6 h-6 animate-pulse" data-id="element-25" />
                    </div>

                    <div className="text-xs font-mono-medical uppercase tracking-widest mb-2 text-[#8B0000]" data-id="element-26">
                      Exhibito Specialis
                    </div>
                    <div className="text-3xl font-medical mb-2 text-[#3E2723]" data-id="element-27">
                      Körperwelten
                    </div>
                    <div className="text-sm font-serif italic text-[#3E2723]/80 mb-4" data-id="element-28">
                      "Am Puls der Zeit"
                    </div>

                    <div className="w-full h-px bg-[#3E2723] opacity-20 my-4" data-id="element-29"></div>

                    <div className="text-xs font-mono-medical text-[#3E2723]" data-id="element-30">
                      ADMIT: TWO (2)
                      <br data-id="element-31" />
                      LOC: VIENNA
                      <br data-id="element-32" />
                      CLICK TO REDEEM{' '}
                      <ExternalLink className="inline w-3 h-3 ml-1" data-id="element-33" />
                    </div>

                    {/* Hover Effect Overlay */}
                    <div className="absolute inset-0 bg-[#8B0000] opacity-0 group-hover:opacity-5 transition-opacity duration-300" data-id="element-34" />
                  </div>
                </div>
              </a>

              <p className="mt-8 text-xs font-mono-medical text-[#3E2723]/50 uppercase tracking-widest" data-id="element-35">
                Specimen No. 2024-KW-VIE • Dr. G. von Hagens
              </p>
            </div>}
        </div>
      </div>

      {/* Surgical Tools (Decorative) */}
      <div className="absolute bottom-4 right-4 opacity-30 pointer-events-none transform rotate-45" data-id="element-36">
        <Scissors className="w-12 h-12 text-[#3E2723]" data-id="element-37" />
      </div>

      {/* Progress Indicators */}
      <div className="absolute bottom-safe-bottom left-0 right-0 flex justify-center gap-4 pb-8 z-20 pointer-events-none" data-id="element-38">
        {STAGES.map((_, idx) => <div key={idx} className="flex flex-col items-center gap-1" data-id="element-39">
            <div className={`h-2 w-2 rounded-full border border-[#3E2723] transition-all duration-500 ${idx <= stage ? 'bg-[#8B0000]' : 'bg-transparent'}`} data-id="element-40" />
            <span className="text-[10px] font-mono-medical text-[#3E2723]/60" data-id="element-41">
              {['I', 'II', 'III', 'IV'][idx]}
            </span>
          </div>)}
      </div>

      {/* Hint */}
      {stage === 0 && <div className="absolute bottom-24 left-0 right-0 text-center animate-pulse pointer-events-none" data-id="element-42">
          <span className="font-mono-medical text-[#3E2723]/40 text-xs tracking-widest uppercase" data-id="element-43">
            [ Tap to Dissect ]
          </span>
        </div>}
    </div>;
}